import { ISelectOption } from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import { Reducer } from 'redux';
import Constants from '../constants';
import {
	AttributeName,
	EditState,
	ICustomQuestionEditModel,
	ICustomResponseOptionEditModel,
	IResponseOptionGroup,
	ITranslationEditItem,
	IValidationCollection,
	IValidationModel,
	QuestionType,
	ResponseOptionGroupViewModel,
} from '../models';
import * as questionLibraryApi from '../services/CommonQuestionAPI';
import * as CustomQuestionAPI from '../services/CustomQuestionAPI';
import { TranslationHelper } from '../utilities';
import { IApplicationState } from './';
import { ActionTypes } from './ActionTypes';
import * as Actions from './Actions';

export interface ICustomQuestionState {
	attributeName?: AttributeName;
	defaultTextValue: string;
	externalAttributeColumnName?: string;
	isMultiple: boolean;
	libraryResponseOptionGroups: ResponseOptionGroupViewModel[];
	loading: boolean;
	parentQuestionId?: number;
	question: ICustomQuestionEditModel;
	questionType: QuestionType;
	responseOptionGroupName: string;
	responseOptionGroupsLoading: boolean;
	responseOptions: ICustomResponseOptionEditModel[];
	responseOptionsGroupTabKey?: string;
	saving: boolean;
	sourceResponseOptionGroupId?: number;
	translations: ITranslationEditItem[];
	unsavedQuestionChanges: boolean;
	validationResults?: IValidationCollection;
}

export const defaultCustomQuestion: ICustomQuestionEditModel = {
	surveyQuestionId: undefined,
	defaultCultureCode: 'en-US',
	surveyId: 0,
	isDemographic: false,
	isMultiple: false,
	questionType: QuestionType.Scale,
	responseOptions: [],
	sourceResponseOptionGroupId: undefined,
	translations: [],
	isActive: true,
	responseOptionGroupName: '',
	dimensions: [],
};

export const defaultState: ICustomQuestionState = {
	loading: false,
	saving: false,
	question: { ...defaultCustomQuestion },
	defaultTextValue: '',
	questionType: QuestionType.Scale,
	translations: [],
	libraryResponseOptionGroups: [],
	responseOptionGroupsLoading: false,
	responseOptions: [],
	responseOptionGroupName: '',
	isMultiple: false,
	unsavedQuestionChanges: false,
};

export const getNewCustomResponseOption = (supportedCultures: string[], availableCultures: ISelectOption[], index: number) => {
	const translations: ITranslationEditItem[] = TranslationHelper.initializeTranslationItems(supportedCultures, availableCultures);
	const responseOption: ICustomResponseOptionEditModel = {
		id: index,
		value: index,
		sequence: index,
		optOut: false,
		hasFollowUp: false,
		translations,
	};
	return responseOption;
};

export const getCustomQuestionEditModelFromState = (state: ICustomQuestionState) => {
	const updatedBaseTranslation = {
		cultureCode: state.question.defaultCultureCode,
		textValue: state.defaultTextValue,
		editState: state.question.surveyQuestionId === undefined ? EditState.Add : EditState.Update,
		language: '',
	};

	const { responseOptions } = state.question;

	const nextResponseOptions = state.responseOptions.map((ro: ICustomResponseOptionEditModel) => {
		const original: ICustomResponseOptionEditModel | undefined = responseOptions.find(
			(x: ICustomResponseOptionEditModel) => x.surveyResponseOptionId === ro.surveyResponseOptionId,
		);
		let nextTranslations: ITranslationEditItem[];
		if (original) {
			nextTranslations = TranslationHelper.updateTranslationEditState(ro.translations, original.translations);
		} else {
			nextTranslations = ro.translations
				.filter((t: ITranslationEditItem) => t.textValue !== '')
				.map((t: ITranslationEditItem) => ({ ...t, editState: EditState.Add }));
		}
		return { ...ro, translations: nextTranslations };
	});

	const questionTranslations = TranslationHelper.updateTranslationEditState(state.translations, state.question.translations);
	return {
		...state.question,
		questionType: state.questionType,
		attributeName: state.attributeName || state.question.attributeName,
		externalAttributeColumnName: state.externalAttributeColumnName || state.question.externalAttributeColumnName,
		sourceResponseOptionGroupId: state.sourceResponseOptionGroupId,
		responseOptionGroupName: state.responseOptionGroupName,
		isMultiple: state.isMultiple,
		responseOptions: nextResponseOptions,
		translations: questionTranslations.concat([updatedBaseTranslation]),
	};
};

export const ActionCreators = {
	GetCustomQuestion:
		(isDemographic: boolean, parentQuestionId?: number, questionId?: number) => (dispatch: any, getState: () => IApplicationState) => {
			dispatch({ type: ActionTypes.CUSTOM_QUESTION_GET_REQUESTED });

			const { defaultCultureCode, supportedCultures, survey } = getState().survey.surveyEditModel;
			const { availableCultures } = getState().cultures;

			if (questionId === undefined) {
				const surveyId = survey.surveyId;
				const responseOptions = isDemographic ? [getNewCustomResponseOption(supportedCultures, availableCultures, 1)] : [];
				dispatch({
					type: ActionTypes.CUSTOM_QUESTION_GET_SUCCEEDED,
					payload: {
						...defaultCustomQuestion,
						translations: TranslationHelper.initializeEditableTranslationItems(defaultCultureCode, availableCultures, supportedCultures),
						isDemographic,
						questionType: QuestionType.Scale,
						surveyId,
						defaultCultureCode,
						responseOptions,
						isMultiple: false,
						responseOptionsGroupTabKey: '',
						parentQuestionId,
					},
				});
			} else {
				return CustomQuestionAPI.getCustomQuestionEditModel(survey.surveyId, questionId)
					.then((customQuestion: ICustomQuestionEditModel) => {
						dispatch({
							type: ActionTypes.CUSTOM_QUESTION_GET_SUCCEEDED,
							payload: {
								...customQuestion,
								defaultCultureCode,
								responseOptionsGroupTabKey: !isDemographic ? customQuestion.responseOptionsGroupTabKey : undefined,
								parentQuestionId: parentQuestionId,
							},
						});
					})
					.catch(() => dispatch({ type: ActionTypes.CUSTOM_QUESTION_GET_FAILED }));
			}
		},
	SaveCustomQuestion: () => (dispatch: any, getState: () => IApplicationState) => {
		dispatch({ type: ActionTypes.CUSTOM_QUESTION_SAVE_REQUESTED });

		const customQuestion = getState().customQuestion;
		const originalAttribute = customQuestion.question.externalAttributeColumnName;
		let editModel = getCustomQuestionEditModelFromState(customQuestion);
		editModel = { ...editModel, responseOptionsGroupTabKey: customQuestion.responseOptionsGroupTabKey };
		const api =
			editModel.surveyQuestionId === undefined || editModel.surveyQuestionId === 0
				? CustomQuestionAPI.addCustomQuestion
				: CustomQuestionAPI.updateCustomQuestion;
		return api(editModel.surveyId, editModel)
			.then((validationModel: IValidationModel<ICustomQuestionEditModel>) => {
				if (validationModel.isValid) {
					const nextAttribute = editModel.externalAttributeColumnName;
					const parentQuestionId = editModel.parentQuestionId;
					dispatch({ type: ActionTypes.CUSTOM_QUESTION_SAVE_SUCCEEDED, payload: { question: validationModel.subject, parentQuestionId } });
					dispatch({
						type: ActionTypes.CLIENT_PARTICIPANT_ATTRIBUTE_REPLACE_SELECTED,
						payload: { original: originalAttribute, next: nextAttribute },
					});
				} else {
					dispatch({
						type: ActionTypes.CUSTOM_QUESTION_SAVE_FAILED,
						payload: validationModel.validationResults,
					});
				}
			})
			.catch((error: Error) => dispatch({ type: ActionTypes.CUSTOM_QUESTION_SAVE_FAILED, payload: { errors: [error] } }));
	},
	ResetCustomQuestion: () => ({ type: ActionTypes.CUSTOM_QUESTION_RESET }),
	HandleQuestionTextChange: (value: string) => (dispatch: any) => {
		dispatch({ type: ActionTypes.CUSTOM_QUESTION_HANDLE_TEXT_CHANGE, payload: value });
	},
	HandleResponseOptionGroupChange: (sourceResponseOptionGroupId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.CUSTOM_QUESTION_HANDLE_ROG_CHANGE, payload: sourceResponseOptionGroupId });
	},
	HandleSetOpenEnded: () => ({ type: ActionTypes.CUSTOM_QUESTION_SET_OPENENDED }),
	HandleSetTextBox: () => ({ type: ActionTypes.CUSTOM_QUESTION_SET_TEXTBOX }),
	HandleTranslationChange: (translations: ITranslationEditItem[]) => (dispatch: any) => {
		dispatch({ type: ActionTypes.CUSTOM_QUESTION_HANDLE_TRANSLATION_CHANGE, payload: translations });
	},
	HandleAttributeNameChange: (externalAttributeColumnName: string, attributeName?: AttributeName) => ({
		type: ActionTypes.CUSTOM_QUESTION_HANDLE_ATTRIBUTE_NAME_CHANGE,
		payload: { externalAttributeColumnName, attributeName },
	}),
	GetResponseOptionGroups: () => (dispatch: any, getState: () => IApplicationState) => {
		dispatch({ type: ActionTypes.CUSTOM_QUESTION_GET_RESPONSEOPTIONGROUPS_REQUESTED });

		const { defaultCultureCode } = getState().survey.surveyEditModel;
		return questionLibraryApi
			.GetResponseOptionGroups(defaultCultureCode)
			.then((responseOptionGroups) => {
				const viewModels: ResponseOptionGroupViewModel[] = responseOptionGroups.map(
					(rog: IResponseOptionGroup) => new ResponseOptionGroupViewModel(rog, 'en-US'),
				);
				dispatch({ type: ActionTypes.CUSTOM_QUESTION_GET_RESPONSEOPTIONGROUPS_SUCCEEDED, payload: viewModels });
			})
			.catch((error: Error) => dispatch({ type: ActionTypes.CUSTOM_QUESTION_GET_RESPONSEOPTIONGROUPS_FAILED }));
	},
	UpdateCustomResponseOptionValue: (value: number | '', responseOptionId: number) => {
		return {
			type: ActionTypes.CUSTOM_QUESTION_RESPONSEOPTION_UPDATE_VALUE,
			payload: {
				value,
				responseOptionId,
			},
		};
	},
	UpdateCustomResponseOptionText: (text: string, cultureCode: string, responseOptionId: number) => {
		return {
			type: ActionTypes.CUSTOM_QUESTION_RESPONSEOPTION_UPDATE_TEXT,
			payload: {
				text,
				cultureCode,
				responseOptionId,
			},
		};
	},
	UpdateCustomResponseOptionGroupName: (name: string) => (dispatch: any) => {
		dispatch({
			type: ActionTypes.CUSTOM_QUESTION_RESPONSEOPTION_UPDATE_GROUP_NAME,
			payload: name,
		});
	},
	ToggleCustomResponseOptionGroupMultiple: (isMultiple: boolean) => (dispatch: any) => {
		dispatch({
			type: ActionTypes.CUSTOM_QUESTION_RESPONSEOPTION_MULTIPLE_TOGGLE,
			payload: isMultiple,
		});
		dispatch({
			type: ActionTypes.CUSTOM_QUESTION_HANDLE_QUESTION_TYPE_CHANGE,
			payload: isMultiple ? QuestionType.Checkbox : QuestionType.Scale,
		});
	},
	ToggleCustomResponseOptionGroupRanking: (isRanking: boolean) => (dispatch: any) => {
		dispatch({
			type: ActionTypes.CUSTOM_QUESTION_HANDLE_QUESTION_TYPE_CHANGE,
			payload: isRanking ? QuestionType.Ranking : QuestionType.Scale,
		});
	},
	UpdateCustomResponseOptionsOrder: (ids: number[]) => {
		return {
			type: ActionTypes.CUSTOM_QUESTION_RESPONSEOPTION_SORT,
			payload: ids,
		};
	},
	AddResponseOption: () => {
		return { type: ActionTypes.CUSTOM_QUESTION_RESPONSEOPTION_ADD };
	},
	DeleteResponseOption: (responseOptionId: number) => {
		return {
			type: ActionTypes.CUSTOM_QUESTION_RESPONSEOPTION_DELETE,
			payload: responseOptionId,
		};
	},
	HandleFollowUpCheck: (responseOptionId: number) => (dispatch: any) => {
		dispatch({
			type: ActionTypes.CUSTOM_QUESTION_HANDLE_FOLLOW_UP_CHECK,
			payload: { responseOptionId: responseOptionId },
		});
	},
	HandleHierarchyChange: (hierarchyId: string) => (dispatch: any) => {
		dispatch({
			type: ActionTypes.CUSTOM_QUESTION_HANDLE_HIERARCHY_CHANGE,
			payload: hierarchyId,
		});
	},
	HandleResponseOptionsGroupTabChange: (responseOptionsGroupTabKey: string) => (dispatch: any, getState: () => IApplicationState) => {
		const { supportedCultures } = getState().survey.surveyEditModel;
		const { availableCultures } = getState().cultures;

		const responseOptions =
			responseOptionsGroupTabKey === Constants.RESPONSE_OPTION_GROUP_TAB_CUSTOM
				? [getNewCustomResponseOption(supportedCultures, availableCultures, 1)]
				: [];
		let questionType = QuestionType.Scale;
		let responseOptionGroupName = '';
		if (responseOptionsGroupTabKey === Constants.RESPONSE_OPTION_GROUP_TAB_HIERARCHY) {
			questionType = QuestionType.Hierarchy;
			responseOptionGroupName = QuestionType[QuestionType.Hierarchy];
		}

		dispatch({ type: ActionTypes.HIERARCHY_RESET_STATE });
		return dispatch({
			type: ActionTypes.CUSTOM_QUESTION_RESPONSE_OPTIONS_GROUP_TAB_CHANGE,
			payload: {
				questionType,
				responseOptions,
				responseOptionGroupName,
				responseOptionsGroupTabKey,
			},
		});
	},
};

type KnownActions =
	| Actions.ICustomQuestionReset
	| Actions.ICustomQuestionGetRequested
	| Actions.ICustomQuestionGetSucceeded
	| Actions.ICustomQuestionGetFailed
	| Actions.ICustomQuestionSaveRequested
	| Actions.ICustomQuestionSaveSucceeded
	| Actions.ICustomQuestionSaveFailed
	| Actions.ICustomQuestionValidationFailed
	| Actions.ICustomQuestionHandleQuestionTextChange
	| Actions.ICustomQuestionHandleRogChange
	| Actions.ICustomQuestionHandleTranslationChange
	| Actions.ICustomQuestionHandleAttributeNameChange
	| Actions.ICustomQuestionHandleQuestionTypeChange
	| Actions.ICustomQuestionResponseOptionGroupsGetFailed
	| Actions.ICustomQuestionResponseOptionGroupsGetRequested
	| Actions.ICustomQuestionResponseOptionGroupsGetSucceeded
	| Actions.ICustomQuestionResponseOptionUpdateText
	| Actions.ICustomQuestionResponseOptionGroupsUpdateGroupName
	| Actions.ICustomQuestionResponseOptionGroupMultipleToggle
	| Actions.ICustomQuestionResponseOptionGroupRankingToggle
	| Actions.ICustomQuestionResponseOptionUpdateValue
	| Actions.ICustomQuestionResponseOptionsSort
	| Actions.ICustomQuestionSetOpenEnded
	| Actions.ICustomQuestionSetTextBox
	| Actions.ICustomQuestionResponseOptionAdd
	| Actions.ICustomQuestionResponseOptionDelete
	| Actions.ICustomQuestionHandleFollowUpCheck
	| Actions.ICustomQuestionHandleHierarchyChange
	| Actions.ICustomQuestionResponseGroupTabChange;

export const reducer: Reducer<ICustomQuestionState, KnownActions> = (
	state: ICustomQuestionState | undefined,
	action: KnownActions,
): ICustomQuestionState => {
	if (!state) {
		state = { ...defaultState };
	}

	switch (action.type) {
		// RESET
		case ActionTypes.CUSTOM_QUESTION_RESET:
			return { ...defaultState };

		// GET EDIT MODEL
		case ActionTypes.CUSTOM_QUESTION_GET_REQUESTED:
			return { ...state, loading: true };

		case ActionTypes.CUSTOM_QUESTION_GET_FAILED:
			return { ...state, loading: false };

		case ActionTypes.CUSTOM_QUESTION_GET_SUCCEEDED: {
			const defaultTranslation =
				_.find(action.payload.translations, (t: ITranslationEditItem) => t.cultureCode === action.payload.defaultCultureCode) || null;

			const translations: ITranslationEditItem[] = TranslationHelper.getEditableTranslations(
				action.payload.translations,
				action.payload.defaultCultureCode,
			);

			const responseOptions = action.payload.responseOptions?.map((ro, index) => ({ ...ro, id: index }));

			return {
				...state,
				loading: false,
				question: { ...action.payload, translations },
				questionType: action.payload.questionType,
				attributeName: action.payload.attributeName,
				translations: _.cloneDeep(translations),
				sourceResponseOptionGroupId: action.payload.sourceResponseOptionGroupId,
				responseOptionGroupName: action.payload.responseOptionGroupName,
				isMultiple: action.payload.isMultiple,
				responseOptions: _.cloneDeep(_.orderBy(responseOptions, 'sequence')),
				defaultTextValue: defaultTranslation === null ? '' : defaultTranslation.textValue,
				validationResults: undefined,
				responseOptionsGroupTabKey: action.payload.responseOptionsGroupTabKey,
				parentQuestionId: action.payload.parentQuestionId,
			};
		}
		// SAVE EDIT MODEL
		case ActionTypes.CUSTOM_QUESTION_SAVE_REQUESTED:
			return { ...state, saving: true, validationResults: undefined };
		case ActionTypes.CUSTOM_QUESTION_SAVE_FAILED:
			return { ...state, saving: false, validationResults: action.payload };
		case ActionTypes.CUSTOM_QUESTION_SAVE_SUCCEEDED: {
			const question = action.payload.question;
			question.parentQuestionId = action.payload.parentQuestionId;
			return {
				...state,
				saving: false,
				question: question,
				validationResults: undefined,
				unsavedQuestionChanges: false,
			};
		}
		case ActionTypes.CUSTOM_QUESTION_VALIDATION_FAILED:
			return { ...state, saving: false, validationResults: action.payload };

		// HANDLING CHANGE EVENTS
		case ActionTypes.CUSTOM_QUESTION_HANDLE_TEXT_CHANGE:
			return {
				...state,
				defaultTextValue: action.payload,
				unsavedQuestionChanges: true,
			};
		case ActionTypes.CUSTOM_QUESTION_HANDLE_ROG_CHANGE:
			return {
				...state,
				questionType: state.question.questionType,
				sourceResponseOptionGroupId: action.payload,
				unsavedQuestionChanges: true,
			};
		case ActionTypes.CUSTOM_QUESTION_HANDLE_TRANSLATION_CHANGE:
			return {
				...state,
				translations: action.payload,
				unsavedQuestionChanges: true,
			};
		case ActionTypes.CUSTOM_QUESTION_HANDLE_ATTRIBUTE_NAME_CHANGE:
			return {
				...state,
				...action.payload,
				unsavedQuestionChanges: true,
			};

		// Library Response Option Groups
		case ActionTypes.CUSTOM_QUESTION_GET_RESPONSEOPTIONGROUPS_FAILED:
			return {
				...state,
				responseOptionGroupsLoading: false,
			};
		case ActionTypes.CUSTOM_QUESTION_GET_RESPONSEOPTIONGROUPS_REQUESTED:
			return {
				...state,
				responseOptionGroupsLoading: true,
			};
		case ActionTypes.CUSTOM_QUESTION_GET_RESPONSEOPTIONGROUPS_SUCCEEDED:
			return {
				...state,
				libraryResponseOptionGroups: action.payload,
				responseOptionGroupsLoading: false,
			};
		case ActionTypes.CUSTOM_QUESTION_RESPONSEOPTION_UPDATE_GROUP_NAME:
			return {
				...state,
				responseOptionGroupName: action.payload,
				unsavedQuestionChanges: true,
			};
		case ActionTypes.CUSTOM_QUESTION_RESPONSEOPTION_MULTIPLE_TOGGLE:
			return {
				...state,
				isMultiple: action.payload,
				unsavedQuestionChanges: true,
			};
		case ActionTypes.CUSTOM_QUESTION_RESPONSE_OPTIONS_RANKING_TOGGLE:
			return {
				...state,
				questionType: action.payload ? QuestionType.Ranking : QuestionType.Scale,
			};
		case ActionTypes.CUSTOM_QUESTION_HANDLE_QUESTION_TYPE_CHANGE:
			return {
				...state,
				questionType: action.payload,
				unsavedQuestionChanges: true,
			};
		// Custom ResponseOption Actions
		case ActionTypes.CUSTOM_QUESTION_RESPONSEOPTION_UPDATE_TEXT: {
			const newTextArray = state.responseOptions.map((ro) => {
				if (ro.id === action.payload.responseOptionId) {
					const localTranslations = ro.translations.map((t) => {
						if (t.cultureCode === action.payload.cultureCode) {
							return { ...t, textValue: action.payload.text };
						}
						return t;
					});
					return { ...ro, translations: localTranslations };
				}
				return ro;
			});
			return { ...state, responseOptions: newTextArray, unsavedQuestionChanges: true };
		}
		case ActionTypes.CUSTOM_QUESTION_RESPONSEOPTION_UPDATE_VALUE: {
			const newValueArray = state.responseOptions.map((ro) => {
				if (ro.id === action.payload.responseOptionId) {
					return { ...ro, value: action.payload.value };
				}
				return ro;
			});
			return { ...state, responseOptions: newValueArray, unsavedQuestionChanges: true };
		}
		case ActionTypes.CUSTOM_QUESTION_RESPONSEOPTION_SORT: {
			const sortedOptions: ICustomResponseOptionEditModel[] = action.payload.map((id, index) => {
				const foundOption = (state as ICustomQuestionState).responseOptions.find((ro) => ro.id === id) as ICustomResponseOptionEditModel;
				return { ...foundOption, sequence: index + 1 };
			});
			return { ...state, responseOptions: sortedOptions, unsavedQuestionChanges: true };
		}
		case ActionTypes.CUSTOM_QUESTION_HANDLE_FOLLOW_UP_CHECK: {
			const updatedResponseOptions: ICustomResponseOptionEditModel[] = state.responseOptions.map((ro: ICustomResponseOptionEditModel) => {
				const hasFollowUpValue: boolean = ro.id === action.payload.responseOptionId ? !ro.hasFollowUp : false;
				return { ...ro, hasFollowUp: hasFollowUpValue };
			});

			return {
				...state,
				responseOptions: updatedResponseOptions,
				unsavedQuestionChanges: true,
			};
		}
		case ActionTypes.CUSTOM_QUESTION_HANDLE_HIERARCHY_CHANGE: {
			return {
				...state,
				question: { ...state.question, hierarchyId: action.payload },
				unsavedQuestionChanges: true,
			};
		}
		case ActionTypes.CUSTOM_QUESTION_RESPONSEOPTION_ADD: {
			// using the first Response Option to get languages available for translation
			const existingTranslations = state.responseOptions[0].translations;
			const newResponseOption = getNewCustomResponseOption(
				existingTranslations.map((t) => t.cultureCode),
				existingTranslations.map((t) => ({ name: t.cultureCode, displayText: t.language })),
				state.responseOptions.length + 1,
			);
			newResponseOption.id = Math.max(...state.responseOptions.map((ro) => ro.id)) + 1;
			newResponseOption.value = Math.max(...state.responseOptions.map((ro) => ro.value || 0)) + 1;

			return { ...state, responseOptions: state.responseOptions.concat(newResponseOption), unsavedQuestionChanges: true };
		}
		case ActionTypes.CUSTOM_QUESTION_SET_TEXTBOX: {
			return {
				...state,
				questionType: QuestionType.TextBox,
				sourceResponseOptionGroupId: undefined,
				unsavedQuestionChanges: true,
			};
		}
		case ActionTypes.CUSTOM_QUESTION_RESPONSEOPTION_DELETE: {
			return {
				...state,
				responseOptions: state.responseOptions.filter((ro) => ro.id !== action.payload),
				unsavedQuestionChanges: true,
			};
		}
		case ActionTypes.CUSTOM_QUESTION_RESPONSE_OPTIONS_GROUP_TAB_CHANGE: {
			const responseOptions = action.payload.responseOptions?.map((ro, index) => ({ ...ro, id: index })) ?? [];
			return {
				...state,
				question: { ...state.question, questionType: action.payload.questionType, hierarchyId: undefined },
				questionType: action.payload.questionType,
				sourceResponseOptionGroupId: undefined,
				responseOptions,
				responseOptionGroupName: action.payload.responseOptionGroupName,
				responseOptionsGroupTabKey: action.payload.responseOptionsGroupTabKey,
			};
		}
		case ActionTypes.CUSTOM_QUESTION_SET_OPENENDED: {
			if (state.question.surveyQuestionId === undefined) {
				return {
					...state,
					questionType: QuestionType.OpenEnded,
					sourceResponseOptionGroupId: undefined,
					unsavedQuestionChanges: true,
				};
			}
		}
	}

	return state;
};
